import webApiClient from "./client";

/**
 * Booking Management API Client
 *
 * Handles private lesson booking operations for Phase 3:
 * - Student booking creation
 * - Instructor booking confirmation/cancellation
 * - Booking retrieval and management
 *
 * Follows V2 API response patterns with explicit semantic field access
 */

/**
 * History page size for the student "My Lessons" list (scope=lessons). Matches the
 * backend default. The server's offset indexes the pageable history only and always
 * returns a full page unless it is the last one, so the next offset is offset + this.
 */
export const LESSONS_HISTORY_PAGE = 30;

export const bookingApi = {
    // ====================
    // Student Operations
    // ====================

    /**
     * Create a new booking (student reserves time slot)
     * NOTE: Location is auto-populated by backend from instructor's default teaching location
     * @param {object} bookingData - Booking details
     * @param {string} bookingData.instructorId - Instructor organization ID
     * @param {string} bookingData.bookingDate - ISO 8601 date (YYYY-MM-DD)
     * @param {object} bookingData.timeSlot - Time slot details
     * @param {string} bookingData.timeSlot.startTime - Start time (HH:mm)
     * @param {string} bookingData.timeSlot.endTime - End time (HH:mm)
     * @param {object} bookingData.studentInfo - Student contact information
     * @param {string} bookingData.studentInfo.firstName - Student first name
     * @param {string} bookingData.studentInfo.lastName - Student last name
     * @param {string} bookingData.studentInfo.email - Student email
     * @param {string} [bookingData.studentInfo.phone] - Student phone (optional)
     * @param {string} [bookingData.studentInfo.message] - Student-initiated only: optional booking-request note from the student (mirrored into booking-messages as the first chat row). MUST be empty for instructor-manual bookings. Never write system / auto-notification text here — backend will send those via the booking-messages chat thread.
     * @returns {Promise<object>} Created booking with status: pending
     * @throws {Error} If creation fails or slot unavailable
     */
    createBooking: async bookingData => {
        const response = await webApiClient.post("/api/v2/booking", bookingData);

        if (!response.ok || !response.data || !response.data.booking) {
            const error = new Error(response.data?.message || "Failed to create booking");
            error.status = response.status;
            error.errorCode = response.data?.code || response.data?.errorCode;
            throw error;
        }

        return response.data.booking; // ✅ Explicit semantic field access
    },

    /**
     * Get booking details by ID (student or instructor only)
     * @param {string} bookingId - Booking ID
     * @returns {Promise<object>} Complete booking details
     * @throws {Error} If fetch fails or unauthorized
     */
    getBookingDetails: async bookingId => {
        const response = await webApiClient.get(`/api/v2/booking/${bookingId}`);

        if (!response.ok || !response.data || !response.data.booking) {
            throw new Error(response.data?.message || "Failed to fetch booking details");
        }

        return response.data.booking;
    },

    /**
     * Get all bookings for current user (student view)
     * @param {object} [options] - Query options
     * @param {string} [options.scope] - Server-side scope. For the student My Lessons
     *   list use getStudentLessons below, which pages the history. Omit for the full
     *   unbounded list.
     * @param {string} [options.status] - Filter by status
     * @param {string} [options.startDate] - Filter start date
     * @param {string} [options.endDate] - Filter end date
     * @returns {Promise<Array>} Array of user's bookings
     * @throws {Error} If fetch fails
     */
    getUserBookings: async (options = {}) => {
        const params = new URLSearchParams();
        params.append("role", "student"); // Specify student role for filtering
        if (options.scope) params.append("scope", options.scope);
        if (options.status) params.append("status", options.status);
        if (options.startDate) params.append("startDate", options.startDate);
        if (options.endDate) params.append("endDate", options.endDate);

        const queryString = params.toString();
        const url = `/api/v2/booking${queryString ? "?" + queryString : ""}`;

        const response = await webApiClient.get(url);

        if (!response.ok || !response.data || !response.data.bookings) {
            throw new Error(response.data?.message || "Failed to fetch user bookings");
        }

        return response.data.bookings;
    },

    /**
     * Get one page of the student "My Lessons" list (scope=lessons).
     * GET /api/v2/booking?role=student&scope=lessons&limit&offset
     *
     * Always returns every active booking plus every history booking that still
     * carries unread messages, so an unread badge derived from the list stays exact
     * on any page. Those rows repeat on every page, so MERGE pages by booking id
     * rather than appending. `offset` indexes the pageable history only.
     *
     * @param {object} [options] - Query options
     * @param {number} [options.offset=0] - History rows already loaded
     * @param {number} [options.limit=30] - History page size
     * @returns {Promise<{bookings: Array, hasMore: boolean}>} Page plus next-page signal
     * @throws {Error} If fetch fails
     */
    getStudentLessons: async (options = {}) => {
        const params = new URLSearchParams();
        params.append("role", "student");
        params.append("scope", "lessons");
        params.append("limit", String(options.limit ?? LESSONS_HISTORY_PAGE));
        params.append("offset", String(options.offset ?? 0));

        const response = await webApiClient.get(`/api/v2/booking?${params.toString()}`);

        if (!response.ok || !response.data || !response.data.bookings) {
            throw new Error(response.data?.message || "Failed to fetch lessons");
        }

        return {
            bookings: response.data.bookings,
            hasMore: response.data.pagination?.hasMore === true,
        };
    },

    /**
     * Cancel a booking (student can cancel pending bookings)
     * @param {string} bookingId - Booking ID
     * @param {string} [reason] - Cancellation reason
     * @returns {Promise<object>} Updated booking
     * @throws {Error} If cancellation fails
     */
    cancelBooking: async (bookingId, reason) => {
        // Cancelling is a state change (not a deletion) — PUT carries the reason
        // reliably in the body. (DELETE-with-body is dropped by some clients.)
        const response = await webApiClient.put(`/api/v2/booking/${bookingId}/cancel`, { reason });

        if (!response.ok || !response.data || !response.data.booking) {
            throw new Error(response.data?.message || "Failed to cancel booking");
        }

        return response.data.booking;
    },

    // ====================
    // Instructor Operations
    // ====================

    /**
     * Get all bookings for instructor (instructor view)
     * Backend filters by authenticated user's organizations automatically
     * @param {object} [options] - Query options
     * @param {string} [options.status] - Filter by status
     * @param {string} [options.startDate] - Filter start date
     * @param {string} [options.endDate] - Filter end date
     * @returns {Promise<Array>} Array of instructor's bookings
     * @throws {Error} If fetch fails
     */
    getInstructorBookings: async (options = {}) => {
        const params = new URLSearchParams();
        params.append("role", "instructor"); // Required: specifies instructor view
        if (options.status) params.append("status", options.status);
        if (options.startDate) params.append("startDate", options.startDate);
        if (options.endDate) params.append("endDate", options.endDate);

        const queryString = params.toString();
        const url = `/api/v2/booking${queryString ? "?" + queryString : ""}`;

        const response = await webApiClient.get(url);

        if (!response.ok || !response.data || !response.data.bookings) {
            throw new Error(response.data?.message || "Failed to fetch instructor bookings");
        }

        return response.data.bookings;
    },

    /**
     * Get a paginated page of the instructor's conversation inbox (scope=inbox).
     *
     * Returns every conversation-bearing booking (any date/status), server-sorted
     * newest-message-first, a page at a time. This is intentionally DECOUPLED from
     * the dashboard's calendar date window — the inbox shows all conversations, not
     * just those on bookings in the currently-viewed month.
     *
     * @param {object} [options]
     * @param {number} [options.limit=30] - Page size
     * @param {number} [options.offset=0] - Rows to skip (for "Load more")
     * @returns {Promise<{ bookings: Array, hasMore: boolean }>}
     * @throws {Error} If fetch fails
     */
    getInstructorInbox: async (options = {}) => {
        const params = new URLSearchParams();
        params.append("role", "instructor");
        params.append("scope", "inbox");
        params.append("limit", String(options.limit ?? 30));
        params.append("offset", String(options.offset ?? 0));

        const response = await webApiClient.get(`/api/v2/booking?${params.toString()}`);

        if (!response.ok || !response.data || !response.data.bookings) {
            throw new Error(response.data?.message || "Failed to fetch inbox");
        }

        return {
            bookings: response.data.bookings,
            hasMore: response.data.pagination?.hasMore === true,
        };
    },

    /**
     * Get unique student roster derived from the instructor's booking history.
     * Aggregates registered students (by studentId) and guests (by email).
     * @returns {Promise<Array>} Array of { studentId, name, email, phone, isGuest, lastBookingDate, nextUpcomingBookingDate, creditBalance }
     * @throws {Error} If fetch fails
     */
    getInstructorStudents: async () => {
        const response = await webApiClient.get("/api/v2/booking/students");

        if (!response.ok || !response.data || !Array.isArray(response.data.students)) {
            throw new Error(response.data?.message || "Failed to fetch instructor students");
        }

        return response.data.students;
    },

    /**
     * Confirm a pending booking (instructor only)
     * @param {string} bookingId - Booking ID
     * @returns {Promise<object>} Updated booking with status: confirmed
     * @throws {Error} If confirmation fails
     */
    confirmBooking: async bookingId => {
        const response = await webApiClient.put(`/api/v2/booking/${bookingId}/approve`);

        if (!response.ok || !response.data || !response.data.booking) {
            throw new Error(response.data?.message || "Failed to confirm booking");
        }

        return response.data.booking;
    },

    /**
     * Reject a pending booking (instructor only)
     * @param {string} bookingId - Booking ID
     * @param {string} [reason] - Rejection reason
     * @returns {Promise<object>} Updated booking with status: rejected
     * @throws {Error} If rejection fails
     */
    rejectBooking: async (bookingId, reason) => {
        const response = await webApiClient.put(`/api/v2/booking/${bookingId}/reject`, { reason });

        if (!response.ok || !response.data || !response.data.booking) {
            throw new Error(response.data?.message || "Failed to reject booking");
        }

        return response.data.booking;
    },

    /**
     * Update booking details (time and/or location) - instructor only
     * Simple mode without nudge - will fail if new time conflicts with existing bookings
     * @param {string} bookingId - Booking ID
     * @param {object} updateData - Update data
     * @param {string} updateData.startTime - New start time (HH:mm)
     * @param {string} updateData.endTime - New end time (HH:mm)
     * @param {object} [updateData.location] - Optional location update
     * @param {string} updateData.location.id - Location ID
     * @param {string} updateData.location.description - Location description
     * @param {string} [updateData.location.address] - Full address
     * @param {number} [updateData.location.latitude] - Latitude
     * @param {number} [updateData.location.longitude] - Longitude
     * @returns {Promise<object>} Updated booking
     * @throws {Error} If update fails or time conflicts with existing bookings
     */
    updateBooking: async (bookingId, updateData) => {
        const response = await webApiClient.patch(`/api/v2/booking/${bookingId}/time`, updateData);

        if (!response.ok || !response.data || !response.data.booking) {
            throw new Error(response.data?.message || "Failed to update booking");
        }

        return response.data.booking;
    },

    /**
     * Apply venue change with optional Stripe payment adjustment for paid bookings.
     * The standard PATCH /time endpoint deliberately skips pricing recalc on paid
     * bookings to preserve original charged amounts. This dedicated endpoint lets
     * the instructor explicitly choose how to handle the price difference.
     *
     * @param {string} bookingId - Booking ID
     * @param {object} payload - { action, newLocationId }
     * @param {'refund'|'charge'|'skip'} payload.action - Refund partial via Stripe,
     *        generate Stripe Checkout link for top-up, or save without payment change
     * @param {string} payload.newLocationId - id from instructor's preferredLocations
     * @param {('chat_message'|'studio_confirmed'|'skip')} payload.path - consent path
     * @param {boolean} [payload.consentConfirmedCheckbox] - required true on studio_confirmed + charge
     * @param {string} [payload.draftMessage] - required on chat_message (studio-edited chat lead-in)
     * @param {string} [payload.studioReason] - optional note
     * @returns {Promise<object>} { success, message, booking, adjustment? }
     *          adjustment shape (when present):
     *            refund: { type:'refund', amountCents, currency, refundId, status, adjustmentId, emailSent }
     *            charge: { type:'charge', amountCents, currency, payUrl, adjustmentId, emailSent, chatMessagePosted }
     *            skip:   { type:'skip', amountCents:0, currency, adjustmentId }
     * @throws {Error} If validation fails, booking unpaid, refund missing
     *         paymentIntent, or guest chat path attempted
     */
    applyVenueChangeAdjustment: async (bookingId, payload) => {
        const response = await webApiClient.post(
            `/api/v2/booking/${bookingId}/venue-change-payment-adjustment`,
            payload
        );

        if (!response.ok || !response.data?.success) {
            throw new Error(
                response.data?.error?.message
                    || response.data?.message
                    || "Failed to process venue change adjustment"
            );
        }

        return response.data; // { success, message, booking, adjustment? }
    },

    /**
     * Update booking time with nudge (instructor only) - shifts following bookings
     * NOTE: Location cannot be changed with nudge operation (use updateBooking instead)
     * @param {string} bookingId - Booking ID
     * @param {object} nudgeData - Nudge update data (plan computed by the frontend planNudge engine)
     * @param {string} nudgeData.startTime - New start time (HH:mm) for this booking
     * @param {string} nudgeData.endTime - New end time (HH:mm) for this booking
     * @param {Array<object>} nudgeData.bookingShifts - Explicit following-booking moves: { bookingId, startTime, endTime }
     * @param {Array<object>} nudgeData.slotShifts - Explicit availability slot moves: { oldStartTime, newStartTime, newEndTime, slotDuration }
     * @returns {Promise<object>} Result with updated booking and shift count
     * @throws {Error} If nudge operation fails
     */
    nudgeBookingTime: async (bookingId, nudgeData) => {
        const response = await webApiClient.patch(`/api/v2/booking/${bookingId}/nudge`, nudgeData);

        if (!response.ok || !response.data || !response.data.booking) {
            throw new Error(response.data?.message || "Failed to nudge booking time");
        }

        return {
            booking: response.data.booking,
            shiftedCount: response.data.shiftedCount || 0,
        };
    },

    // ====================
    // Booking Messages
    // ====================

    /**
     * Send message in booking conversation
     * @param {string} bookingId - Booking ID
     * @param {string} message - Message content (max 1000 chars)
     * @returns {Promise<object>} Sent message object
     * @throws {Error} If send fails
     */
    sendMessage: async (bookingId, message) => {
        const response = await webApiClient.post(`/api/v2/booking/${bookingId}/messages`, { message });

        if (!response.ok || !response.data || !response.data.messageData) {
            throw new Error(response.data?.message || "Failed to send message");
        }

        return response.data.messageData;
    },

    /**
     * Get conversation messages with pagination
     * @param {string} bookingId - Booking ID
     * @param {object} [params] - Pagination parameters
     * @param {number} [params.limit] - Messages per page (default: 100)
     * @param {number} [params.offset] - Offset for pagination (default: 0)
     * @returns {Promise<object>} Messages and pagination info
     * @throws {Error} If fetch fails
     */
    getMessages: async (bookingId, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit) queryParams.append("limit", params.limit.toString());
        if (params.offset) queryParams.append("offset", params.offset.toString());

        const queryString = queryParams.toString();
        const url = queryString
            ? `/api/v2/booking/${bookingId}/messages?${queryString}`
            : `/api/v2/booking/${bookingId}/messages`;

        const response = await webApiClient.get(url);

        if (!response.ok || !response.data || !response.data.messages || !response.data.pagination) {
            throw new Error(response.data?.message || "Failed to fetch messages");
        }

        return {
            messages: response.data.messages,
            pagination: response.data.pagination,
        };
    },

    /**
     * Mark all messages as read for booking
     * @param {string} bookingId - Booking ID
     * @returns {Promise<number>} Count of messages marked as read
     * @throws {Error} If operation fails
     */
    markAllMessagesAsRead: async bookingId => {
        const response = await webApiClient.put(`/api/v2/booking/${bookingId}/messages/read-all`);

        if (!response.ok || !response.data || response.data.markedAsRead === undefined) {
            throw new Error(response.data?.message || "Failed to mark all messages as read");
        }

        return response.data.markedAsRead;
    },

    /**
     * Get total unread message count for user
     * @returns {Promise<number>} Total unread count across all bookings
     * @throws {Error} If fetch fails
     */
    getUnreadCount: async () => {
        const response = await webApiClient.get("/api/v2/booking/messages/unread-count");

        if (!response.ok || !response.data || response.data.unreadCount === undefined) {
            throw new Error(response.data?.message || "Failed to fetch unread count");
        }

        return response.data.unreadCount;
    },

    // ====================
    // Manual Booking User Linking
    // ====================

    /**
     * Link a user account to a manual booking
     * Allows instructors to connect manually-created bookings to registered users
     * @param {string} bookingId - Booking ID
     * @param {string} userId - User account ID to link
     * @returns {Promise<object>} Updated booking with linked user
     * @throws {Error} If linking fails or user not found
     */
    linkUserToBooking: async (bookingId, userId) => {
        const response = await webApiClient.patch(`/api/v2/booking/${bookingId}/link-user`, { userId });

        if (!response.ok || !response.data || !response.data.booking) {
            throw new Error(response.data?.message || "Failed to link user to booking");
        }

        return response.data.booking;
    },

    // ====================
    // Guest Booking Flow (Unauthenticated)
    // ====================

    /**
     * Reserve a slot for a guest (unauthenticated) before payment.
     * Holds the slot for 10 minutes while the guest completes Stripe checkout.
     * POST /api/v2/booking/reserve
     * @param {object} reservationData
     * @param {string} reservationData.instructorId - Instructor organisation ID
     * @param {string} reservationData.bookingDate - ISO 8601 date (YYYY-MM-DD)
     * @param {object} reservationData.timeSlot - { startTime, endTime }
     * @param {object} reservationData.studentInfo - { name, email, phone?, message? }
     * @returns {Promise<object>} { reservationId, expiresAt, bookingPreview }
     * @throws {Error} If slot unavailable or validation fails
     */
    reserveSlot: async reservationData => {
        const response = await webApiClient.post("/api/v2/booking/reserve", reservationData);

        if (!response.ok) {
            const error = new Error(response.data?.message || "Failed to reserve slot");
            error.status = response.status;
            error.errorCode = response.data?.code || response.data?.errorCode;
            throw error;
        }

        return response.data;
    },

    /**
     * Confirm a guest booking after successful Stripe payment.
     * Converts the reservation into a permanent booking. The Stripe paymentIntent
     * is recorded on the `transactions` collection by the Stripe webhook.
     * POST /api/v2/booking/from-reservation
     * @param {string} reservationId - Reservation ID from reserveSlot
     * @returns {Promise<object>} Confirmed booking object
     * @throws {Error} If reservation expired or invalid
     */
    confirmFromReservation: async (reservationId) => {
        const response = await webApiClient.post("/api/v2/booking/from-reservation", {
            reservationId,
        });

        if (!response.ok || !response.data?.booking) {
            throw new Error(response.data?.message || "Failed to confirm booking from reservation");
        }

        return response.data.booking;
    },

    /**
     * Mark an authenticated student's before-lesson booking as paid after Stripe
     * checkout succeeds. The Stripe paymentIntent is recorded on the `transactions`
     * collection by the Stripe webhook.
     * POST /api/v2/booking/:id/record-payment
     * @param {string} bookingId - Booking ID
     * @returns {Promise<object>} Updated booking with paymentStatus: 'paid'
     * @throws {Error} If recording fails
     */
    recordPayment: async (bookingId) => {
        const response = await webApiClient.post(`/api/v2/booking/${bookingId}/record-payment`, {});

        if (!response.ok || !response.data?.success || !response.data.booking) {
            throw new Error(response.data?.message || "Failed to record payment");
        }

        return response.data.booking;
    },

    // Note: completeBooking endpoint removed - not supported by backend contract
    // Bookings are marked as completed automatically by the backend based on lesson date/time

    // ====================
    // Session & Settings
    // ====================

    /**
     * Validate a booking session token before proceeding to payment
     * Returns raw response — callers must inspect response.status for 404/409/410 specifically.
     * @param {string} sessionId - Session token to validate
     * @returns {Promise<object>} Raw response — check response.ok and response.status
     */
    validateBookingSession: async sessionId => {
        return webApiClient.post("/api/v2/booking/sessions/validate", { sessionId });
    },

    /**
     * Get booking settings for the authenticated organisation
     * @returns {Promise<object>} Booking settings object
     * @throws {Error} If fetch fails
     */
    getBookingSettings: async () => {
        const response = await webApiClient.get("/api/v2/booking/settings");

        if (!response.ok || !response.data || !response.data.bookingSettings) {
            throw new Error(response.data?.message || "Failed to fetch booking settings");
        }

        return response.data.bookingSettings;
    },

    /**
     * Update booking settings for the authenticated organisation
     * @param {object} settings - Settings fields to update
     * @returns {Promise<object>} Response data from the update
     * @throws {Error} If update fails
     */
    updateBookingSettings: async settings => {
        const response = await webApiClient.put("/api/v2/booking/settings", settings);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to update booking settings");
        }

        return response.data;
    },

    // Magic-link payment flow (public endpoints — no auth required)
    resolveBookingPaymentToken: (token) => webApiClient.get(`/api/v2/booking/payments/qr/${token}`),
    createBookingPaymentIntent: (bookingId) => webApiClient.post('/api/v2/booking/payments/create-intent', { bookingId }),
    // Venue-change top-up: PaymentIntent for the delta only (booking must have a pending adjustment).
    createBookingAdjustmentIntent: (bookingId) => webApiClient.post(`/api/v2/booking/payments/${bookingId}/adjustment-intent`, {}),

    /**
     * Instructor: mint a fresh payment QR token for a booking.
     * Always invalidates the previous token. Optional customAmount overrides
     * the booking's lesson fee for this link only (integer cents).
     * @param {string} bookingId
     * @param {number} [customAmount] — non-negative integer cents
     * @returns {Promise<{ qrToken: string, qrUrl: string, qrExpiresAt: string, totalDue: number, currency: string }>}
     */
    generatePaymentQr: async (bookingId, customAmount) => {
        const body = customAmount !== undefined ? { customAmount } : {};
        const response = await webApiClient.post(
            `/api/v2/booking/payments/${bookingId}/generate-qr`,
            body,
        );
        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || 'Failed to generate QR');
        }
        return {
            qrToken: response.data.qrToken,
            qrUrl: response.data.qrUrl,
            qrExpiresAt: response.data.qrExpiresAt,
            totalDue: response.data.totalDue,
            currency: response.data.currency,
        };
    },

    /**
     * Instructor: mint a fresh token AND send the booking-payment email
     * to the booking's studentInfo.email. Optional customAmount overrides
     * the amount for this link only.
     * @param {string} bookingId
     * @param {number} [customAmount]
     * @returns {Promise<{ sent: true, email: string }>}
     */
    sendPaymentEmail: async (bookingId, customAmount) => {
        const body = customAmount !== undefined ? { customAmount } : {};
        const response = await webApiClient.post(
            `/api/v2/booking/payments/${bookingId}/send-email`,
            body,
        );
        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || 'Failed to send payment email');
        }
        return { sent: !!response.data.sent, email: response.data.email };
    },

    /**
     * Instructor: mark a booking paid by bank transfer (cash / EFTPOS also acceptable
     * if the org has bank transfer offering enabled). Flips pricing.paymentStatus → 'paid'
     * with paymentMethod: 'bank_transfer'.
     * POST /api/v2/booking/:id/mark-bank-transfer
     */
    markBankTransfer: async (bookingId, method = 'bank_transfer', notes = null) => {
        const response = await webApiClient.post(`/api/v2/booking/${bookingId}/mark-bank-transfer`, { method, notes });
        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || 'Failed to mark booking as paid');
        }
        return response.data;
    },

    /**
     * Instructor: issue a Stripe refund or platform credit for a paid booking.
     * POST /api/v2/booking/:bookingId/return
     * @param {string} bookingId
     * @param {{ type: 'refund'|'credit', amount?: number }} params - amount in minor units (cents)
     * @returns {Promise<{ returnStatus, refundId?, creditBalance?, currency? }>}
     */
    issueReturn: async (bookingId, { type, amount, message }) => {
        const body = { type };
        if (amount !== undefined) body.amount = amount;
        if (message) body.message = message;
        const response = await webApiClient.post(`/api/v2/booking/${bookingId}/return`, body);
        if (!response.ok || !response.data) {
            throw new Error(response.data?.error?.message || 'Failed to issue return');
        }
        return response.data;
    },

    /**
     * Instructor: get credit balance for a student scoped to the authenticated instructor.
     * GET /api/v2/booking/students/:studentId/credit-balance
     * @param {string} studentId - studentProfileId
     * @returns {Promise<{ creditBalance: number, currency: string, entries: Array }>}
     */
    getCreditBalance: async studentId => {
        const response = await webApiClient.get(`/api/v2/booking/students/${studentId}/credit-balance`);
        if (!response.ok || !response.data) {
            throw new Error(response.data?.error?.message || 'Failed to get credit balance');
        }
        return {
            creditBalance: response.data.creditBalance,
            currency: response.data.currency,
            entries: response.data.entries,
        };
    },

    /**
     * Instructor: apply a student's credit balance when creating a manual booking.
     * POST /api/v2/booking/manual/apply-credit
     * @param {{ studentId: string, bookingId: string, creditAmountToApply: number }} params
     * @returns {Promise<{ appliedAmount: number, remainingBalance: number, currency: string }>}
     */
    applyCredit: async ({ studentId, bookingId, creditAmountToApply }) => {
        const response = await webApiClient.post(`/api/v2/booking/manual/apply-credit`, {
            studentId,
            bookingId,
            creditAmountToApply,
        });
        if (!response.ok || !response.data) {
            throw new Error(response.data?.error?.message || 'Failed to apply credit');
        }
        return {
            appliedAmount: response.data.appliedAmount,
            remainingBalance: response.data.remainingBalance,
            currency: response.data.currency,
            fullyPaid: response.data.fullyPaid,
        };
    },
};
